import React, { forwardRef, ReactNode } from "react";
import {
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableCellProps,
	TableContainer,
	TableHead,
	TableProps,
	TableRow,
	TableRowProps,
	TableSortLabel,
	Typography,
} from "@mui/material";
import { CorePagination, CoreSpinner, TableSortLabelIcon } from "../../../../packages/components/src/core";
import styles from "./composite-table.module.css";
import NoResults from "../images/table-no-results-available.svg?react";

export type CompositeTableHeaderColumn<Key extends string, SortableKey extends Key> = (
	| { key: SortableKey; sortable: true }
	| {
			key: Key;
			sortable?: false;
	  }
) & {
	content: ReactNode;
} & Omit<TableCellProps, "variant" | "children" | "content">;

export default function CompositeTable({
	head,
	children,
	...tableProps
}: {
	head: ReactNode;
} & TableProps) {
	return (
		<Table {...tableProps}>
			<TableHead className={styles.tableHeader}>{head}</TableHead>
			<TableBody>{children}</TableBody>
		</Table>
	);
}

export function CompositeTableContainer({ className, children }: { className?: string; children: ReactNode }) {
	return (
		<TableContainer
			tabIndex={0}
			className={`${styles.tableWrapper} ${className || ""}`}
			onKeyDown={(e) => {
				if (e.key === "ArrowUp") {
					e.currentTarget.scrollTop -= 50;
					e.preventDefault();
				} else if (e.key === "ArrowDown") {
					e.currentTarget.scrollTop += 50;
					e.preventDefault();
				}
			}}
		>
			{children}
		</TableContainer>
	);
}

function CompositeTableHeaderCell({ className, children, ...tableCellProps }: Omit<TableCellProps, "variant">) {
	return (
		<TableCell {...tableCellProps} variant="head" className={`${styles.tableHeaderCell} ${className || ""}`}>
			{children}
		</TableCell>
	);
}

type SortDirection = "asc" | "desc";

export function CompositeTableHeaderRow<
	Key extends string,
	SortableKey extends Key,
	SortRequired extends boolean | undefined,
>({
	columns,
	sortRequired,
	sortBy,
	onSortByChange,
}: {
	columns: CompositeTableHeaderColumn<Key, SortableKey>[];
	sortRequired?: SortRequired;
} & (
	| {
			sortBy: { key: SortableKey; direction: SortDirection }[];
			onSortByChange: (sort: { key: SortableKey; direction: SortDirection }[]) => void;
	  }
	| (SortRequired extends true
			? {
					sortBy: { key: SortableKey; direction: SortDirection };
					onSortByChange: (sort: { key: SortableKey; direction: SortDirection }) => void;
				}
			: {
					sortBy?: { key: SortableKey; direction: SortDirection };
					onSortByChange?: (sort?: { key: SortableKey; direction: SortDirection }) => void;
				})
)) {
	const sortDirections: Partial<Record<SortableKey, SortDirection>> = {};
	if (sortBy) {
		if (Array.isArray(sortBy)) {
			sortBy.forEach(({ key, direction }) => {
				sortDirections[key] = direction;
			});
		} else {
			sortDirections[sortBy.key] = sortBy.direction;
		}
	}

	return (
		<TableRow>
			{columns.map(({ key, content, sortable, width, sx, ...tableCellProps }) => {
				let headerContent: ReactNode = content;
				const sortDirection = sortable ? sortDirections[key] : undefined;
				if (onSortByChange && sortable) {
					headerContent = (
						<TableSortLabel
							active={!!sortDirection}
							direction={sortDirection}
							onClick={() => {
								if (Array.isArray(sortBy)) {
									const onMultiSortChange = onSortByChange as (
										sort: {
											key: SortableKey;
											direction: SortDirection;
										}[],
									) => void;
									if (sortDirection) {
										const setSortDirection = (newDirection: SortDirection) =>
											onMultiSortChange(
												sortBy.map((sort) =>
													sort.key === key
														? {
																key,
																direction: newDirection,
															}
														: sort,
												),
											);

										if (sortDirection === "asc") {
											setSortDirection("desc");
										} else if (sortRequired && sortBy.length === 1) {
											setSortDirection("asc");
										} else {
											onMultiSortChange(sortBy.filter((sort) => sort.key !== key));
										}
									} else {
										onMultiSortChange(sortBy.concat([{ key, direction: "asc" }]));
									}
								} else {
									const onSingleSortChange = onSortByChange as (sort: {
										key: SortableKey;
										direction: SortDirection;
									}) => void;
									if (sortDirection) {
										if (sortDirection === "asc") {
											onSingleSortChange({ key, direction: "desc" });
										} else if (sortRequired) {
											onSingleSortChange({ key, direction: "asc" });
										} else {
											onSingleSortChange(undefined as any);
										}
									} else {
										onSingleSortChange({ key, direction: "asc" });
									}
								}
							}}
							IconComponent={TableSortLabelIcon}
						>
							{headerContent}
						</TableSortLabel>
					);
				}
				return (
					<CompositeTableHeaderCell
						key={key}
						{...tableCellProps}
						sx={{ width, ...sx }}
						aria-sort={sortDirection ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
					>
						{headerContent}
					</CompositeTableHeaderCell>
				);
			})}
		</TableRow>
	);
}

export const CompositeTableBodyRow = forwardRef<HTMLTableRowElement, Omit<TableRowProps, "onKeyDown">>(
	function SessionTableBodyRow({ onClick, children, sx, role, ...tableRowProps }, ref) {
		return (
			<TableRow
				hover
				{...tableRowProps}
				role={role ?? (onClick ? "button" : undefined)}
				onClick={onClick}
				tabIndex={onClick ? 0 : undefined}
				onKeyDown={
					onClick
						? (e) => {
								if (e.key === "Enter" || e.key === " ") {
									onClick(e as unknown as React.MouseEvent<HTMLTableRowElement>);
								}
							}
						: undefined
				}
				sx={{
					...sx,
					cursor: onClick ? "pointer" : undefined,
					"&:focus-visible": onClick
						? {
								outline: "2px solid #0b6f85",
								outlineOffset: "-2px",
							}
						: undefined,
				}}
				ref={ref}
			>
				{children}
			</TableRow>
		);
	},
);

export function CompositeTableCell({ className, children, ...tableCellProps }: TableCellProps) {
	return (
		<TableCell {...tableCellProps} className={`${styles.tableCell} ${className || ""}`}>
			{children}
		</TableCell>
	);
}

export function CompositeTablePaginationWrapper({
	children,
	className,
	tableWrapperClassName,
	page,
	onChange,
	count,
}: {
	children: ReactNode;
	className?: string;
	tableWrapperClassName?: string;
	page: number;
	onChange: (newPage: number) => void;
	count: number;
}) {
	return (
		<div className={`${styles.paginationWrapper} ${className || ""}`}>
			<CompositeTableContainer className={tableWrapperClassName}>{children}</CompositeTableContainer>
			<CorePagination
				page={page + 1}
				onChange={(_, newPage) => onChange(newPage - 1)}
				count={count}
				className={styles.pagination}
			/>
		</div>
	);
}

export function CompositeTableNoResults({ header, children }: { header?: ReactNode; children: ReactNode }) {
	return (
		<div className={styles.noResultsWrapper}>
			<NoResults />
			{header && <Typography className={styles.noResultsHeader}>{header}</Typography>}
			{children}
		</div>
	);
}

export function CompositeTableSpinner() {
	return (
		<div className={styles.spinnerWrapper}>
			<CoreSpinner size={100} />
		</div>
	);
}

export function CompositeTableCellSkeleton(tableCellProps: Omit<TableCellProps, "children">) {
	return (
		<CompositeTableCell {...tableCellProps}>
			<Skeleton variant="text" width="100%" />
		</CompositeTableCell>
	);
}

export function CompositeTableSkeleton({
	columns,
	rows = 5,
}: {
	columns: CompositeTableHeaderColumn<string, string>[];
	rows?: number;
}) {
	return (
		<CompositeTable head={<CompositeTableHeaderRow columns={columns} />}>
			{Array.from(new Array(rows), (_, index) => (
				<CompositeTableBodyRow key={index}>
					{columns.map(({ key, content: _content, sortable: _sortable, ...tableCellProps }) => (
						<CompositeTableCellSkeleton key={key} {...tableCellProps} />
					))}
				</CompositeTableBodyRow>
			))}
		</CompositeTable>
	);
}
